import { useEffect, useState } from 'react';
import { AlertCircle, Package, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { irancharsouqAdminApi } from '../../api/irancharsouqAdminApi';
import { formatNumber, formatToman } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

const EMPTY_PRODUCT = {
  name_fa: '', name_en: '', brand: '', description: '', base_price: '',
  stock_quantity: '0', image_url: '', is_perishable: false,
  is_kalabarg_eligible: false, source_name: '', category_ids: [],
};

export default function AdminProducts() {
  const [data, setData] = useState({ items: [] });
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const load = async () => {
    setState('loading');
    setError('');
    try {
      setData(await irancharsouqAdminApi.getProducts({ page: 1, size: 50 }));
      setState('ready');
    } catch (requestError) {
      console.error(requestError);
      setError('خطا در دریافت محصولات');
      setState('error');
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
    setForm(EMPTY_PRODUCT);
    setError('');
  };

  const openEdit = (product) => {
    setEditing(product);
    setShowForm(true);
    setForm({
      ...EMPTY_PRODUCT,
      ...product,
      base_price: String(product.base_price ?? ''),
      stock_quantity: String(product.stock_quantity ?? 0),
      category_ids: product.categories?.map((category) => category.id) ?? [],
    });
    setError('');
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const result = await irancharsouqAdminApi.uploadImage(file);
      setForm((current) => ({ ...current, image_url: result.url }));
    } catch (requestError) {
      console.error(requestError);
      setError('بارگذاری تصویر با خطا مواجه شد');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      base_price: Number(form.base_price),
      stock_quantity: Number(form.stock_quantity),
    };
    try {
      if (editing) await irancharsouqAdminApi.updateProduct(editing.id, payload);
      else await irancharsouqAdminApi.createProduct(payload);
      setEditing(null);
      setShowForm(false);
      setForm(EMPTY_PRODUCT);
      await load();
    } catch (requestError) {
      console.error(requestError);
      setError('ذخیره محصول با خطا مواجه شد');
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (product) => {
    if (!window.confirm(`حذف محصول ${persianDisplayText(product.name_fa, 'نام ثبت‌نشده')}؟`)) return;
    try {
      await irancharsouqAdminApi.deleteProduct(product.id);
      await load();
    } catch (requestError) {
      console.error(requestError);
      setError('حذف محصول با خطا مواجه شد');
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">محصولات</h1>
          <p className="admin-page-subtitle">ایجاد، ویرایش و حذف محصولات ایران چارسوق</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="admin-refresh-btn" disabled={state === 'loading'}><RefreshCw size={16} /><span>بروزرسانی</span></button>
          <button onClick={openCreate} className="admin-refresh-btn"><Plus size={16} /><span>محصول جدید</span></button>
        </div>
      </div>

      {error && <div className="admin-table-error"><AlertCircle size={16} />{error}</div>}

      {showForm && (
        <form className="card admin-product-form" onSubmit={saveProduct}>
          <div className="admin-page-header"><h2 className="admin-page-title">{editing ? 'ویرایش محصول' : 'ایجاد محصول'}</h2><button type="button" onClick={() => { setEditing(null); setShowForm(false); setForm(EMPTY_PRODUCT); }} className="icon-btn-sm"><X size={18} /></button></div>
          <div className="admin-product-form-grid">
            <label>نام فارسی<input name="name_fa" value={form.name_fa} onChange={handleChange} required /></label>
            <label>نام لاتین برای جست‌وجو<input name="name_en" value={form.name_en} onChange={handleChange} required /></label>
            <label>برند<input name="brand" value={form.brand} onChange={handleChange} /></label>
            <label>قیمت پایه<input name="base_price" type="number" min="0" step="0.01" value={form.base_price} onChange={handleChange} required /></label>
            <label>موجودی<input name="stock_quantity" type="number" min="0" value={form.stock_quantity} onChange={handleChange} required /></label>
            <label>
              تصویر محصول
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} disabled={uploadingImage} />
            </label>
          </div>
          {form.image_url && <img className="admin-product-image-preview" src={form.image_url} alt={form.name_fa || 'تصویر محصول'} />}
          <label className="admin-product-description">توضیحات<textarea name="description" value={form.description} onChange={handleChange} /></label>
          <div className="admin-product-checks">
            <label className={`admin-toggle-card ${form.is_perishable ? 'active' : ''}`}>
              <input name="is_perishable" type="checkbox" checked={form.is_perishable} onChange={handleChange} />
              <span className="admin-toggle-track"><span className="admin-toggle-thumb" /></span>
              <span>فاسدشدنی</span>
            </label>
            <label className={`admin-toggle-card ${form.is_kalabarg_eligible ? 'active' : ''}`}>
              <input name="is_kalabarg_eligible" type="checkbox" checked={form.is_kalabarg_eligible} onChange={handleChange} />
              <span className="admin-toggle-track"><span className="admin-toggle-thumb" /></span>
              <span>مشمول کالابرگ</span>
            </label>
          </div>
          <button type="submit" className="admin-refresh-btn" disabled={saving || uploadingImage}>{uploadingImage ? 'در حال بارگذاری تصویر...' : saving ? 'در حال ذخیره...' : 'ذخیره محصول'}</button>
        </form>
      )}

      <div className="card admin-table-card"><div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>محصول</th><th>برند</th><th>قیمت</th><th>موجودی</th><th>عملیات</th></tr></thead><tbody>
        {state === 'loading' ? <tr><td colSpan="5" className="admin-table-empty">در حال دریافت اطلاعات...</td></tr> : data.items?.length ? data.items.map((product) => <tr key={product.id}><td><span className="admin-cell-primary">{product.image_url && <img className="admin-product-list-image" src={product.image_url} alt="" />}<Package size={14} /> {persianDisplayText(product.name_fa, 'نام ثبت‌نشده')}</span></td><td className="admin-cell-secondary">{persianDisplayText(product.brand)}</td><td>{product.base_price == null ? '—' : formatToman(product.base_price)}</td><td>{formatNumber(product.stock_quantity ?? 0)}</td><td><button className="btn-sm btn-sm-success" onClick={() => openEdit(product)}><Pencil size={14} />ویرایش</button><button className="btn-sm btn-sm-danger" onClick={() => removeProduct(product)}><Trash2 size={14} />حذف</button></td></tr>) : <tr><td colSpan="5" className="admin-table-empty">محصولی یافت نشد.</td></tr>}
      </tbody></table></div></div>
    </div>
  );
}
